import React from 'react';
import { IStudio } from '../../const/studio';
import { STUDIO_ICON_MAP } from '../../const/iconMap';
import InfoIcon from '@/pages/dashboard/assets/svg/sth/infomation.svg';
import GameIcon from '@/pages/dashboard/assets/svg/sth/games.svg';
import EngineIcon from '@/pages/dashboard/assets/svg/sth/engine.svg';
import RatingIcon from '@/pages/dashboard/assets/svg/sth/star.svg';
import DotIcon from '@/pages/dashboard/assets/svg/sth/dot-icon.svg';
import './index.scss';
import classnames from 'classnames';
import CommonBubble from '@home/components/CommonBubble';

interface IStudioCardProps {
  studio: IStudio;
  position: 'top' | 'left' | 'right' | 'bottom';
  onHover?: (studioName: string | string[]) => void;
  onLeave?: () => void;
  isSelected: boolean | string | string[] | null;
}

const StudioCard = React.forwardRef<HTMLDivElement, IStudioCardProps>(
  ({ studio, position, onHover, onLeave, isSelected }, ref) => {
    const {
      name,
      est,
      country,
      employee_info: employeeInfo,
      games,
      game_engine: gameEngine,
      ratings,
      sub_studio: subStudio,
      studio_info: studioInfo
    } = studio;

    const hasSubStudio = !!subStudio;

    // 检查是否被选中（支持 boolean、string 和 string[]）
    const isSelectedState = (() => {
      if (Array.isArray(isSelected)) {
        // 检查主 studio 或 sub_studio 是否被选中
        return isSelected.includes(name) || (subStudio && isSelected.includes(subStudio.name));
      }
      if (typeof isSelected === 'string') {
        return isSelected === name || (subStudio && isSelected === subStudio.name);
      }
      return isSelected;
    })();

    // 渲染星级（支持半星）
    const renderStars = (rating: number) => {
      const stars = [];
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating % 1 !== 0;

      // 渲染满星
      for (let i = 0;i < fullStars;i++) {
        stars.push(
          <img key={`full-${i}`} src={RatingIcon} alt="full-star" />
        );
      }

      // 渲染半星
      if (hasHalfStar) {
        stars.push(
          <div key="half-star" className="dbd-studio-card__half-star">
            <img src={RatingIcon} alt="half-star" />
          </div>
        );
      }

      return stars;
    };

    const handleMouseEnter = () => {
      // 如果有 sub_studio，同时传递两个 studio 名称
      if (hasSubStudio && subStudio) {
        onHover?.([name, subStudio.name]);
      } else {
        onHover?.(name);
      }
    };

    const handleMouseLeave = () => {
      onLeave?.();
    };

    return (
      <CommonBubble
        content={studioInfo}
        placement="top"
      >
        <div
          ref={ref}
          className={classnames(`dbd-studio-card`, {
            'dbd-studio-card--selected': isSelectedState,
          })}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-position={position}
        >
          <div className={classnames(`dbd-studio-card__dot ${position}`, {
            "dbd-studio-card__dot--sub": hasSubStudio
          })}>
            {
              isSelectedState && (
                <img id={name} src={DotIcon} />
              )
            }
            {
              hasSubStudio && isSelectedState && (<img id={subStudio.name} src={DotIcon} />)
            }
          </div>

          {/* 顶部：Logo + 名称 + 年份国家 */}
          <div className="dbd-studio-card__header">
            {/* 第二个工作室（仅在有 subStudio 时显示） */}
            {hasSubStudio && subStudio && (
              <div className="dbd-studio-card__sub-studio">
                <img
                  className="dbd-studio-card__logo"
                  src={STUDIO_ICON_MAP[subStudio.name]}
                  alt={subStudio.name}
                />
                <div className="dbd-studio-card__meta-info">
                  <div className="dbd-studio-card__name">{subStudio.name}</div>
                  <div className="dbd-studio-card__location">{subStudio.est} {subStudio.country}</div>
                </div>
              </div>
            )}

            {/* 第一个工作室 */}
            <div className="dbd-studio-card__studio">
              <div className="dbd-studio-card__name-wrapper">
                <img
                  className="dbd-studio-card__logo"
                  src={STUDIO_ICON_MAP[name]}
                  alt={name}
                />
                <div className="dbd-studio-card__meta-info">
                  <div className="dbd-studio-card__name">{name}</div>
                  <div className="dbd-studio-card__location">{est} {country}</div>
                </div>
              </div>
              {/* 星级评分 - 右上角 */}
              <div className="dbd-studio-card__stars">
                {renderStars(ratings)}
              </div>
            </div>
          </div>

          {/* 底部信息区 */}
          <div className="dbd-studio-card__info">
            {/* 员工信息 */}
            <div className="dbd-studio-card__row">
              <img src={InfoIcon} alt="info" />
              <span>{employeeInfo}</span>
            </div>

            {/* 游戏列表 */}
            <div className="dbd-studio-card__row">
              <img src={GameIcon} alt="games" />
              <span>{games?.join(', ')}</span>
            </div>

            {/* 游戏引擎 */}
            <div className="dbd-studio-card__row">
              <img src={EngineIcon} alt="engine" />
              <span>{gameEngine?.join(' + ')}</span>
            </div>
          </div>
        </div>
      </CommonBubble>
    );
  },
);

StudioCard.displayName = 'StudioCard';

export default StudioCard;
